import { useEffect, useMemo, useState } from 'react'
import { Section, TableScroller, useBearer, usePagedQuery, useToast } from './_admin-utils'

interface ReimbursementItem {
  id: string
  description: string
  amount_cents: number
  receipt_url?: string
}

interface ReimbursementRequest {
  id: string
  teacher_id: string
  status: string
  total_cents: number
  submitted_at?: string
  purchase_request_items?: ReimbursementItem[]
}

const STATUS_OPTIONS = ['all', 'submitted', 'approved', 'paid', 'rejected']

function formatCurrency(cents: number | null | undefined): string {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100)
}

export default function AdminReimbursements() {
  const bearer = useBearer()
  const [status, setStatus] = useState('submitted')
  const [teacherId, setTeacherId] = useState('')
  const [selected, setSelected] = useState<ReimbursementRequest | null>(null)
  const { show, ToastHost } = useToast()

  const fetcher = useMemo(() => {
    return async ({ page, pageSize }: { page: number; pageSize: number }) => {
      if (!bearer) return { items: [], total: 0 }
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (status && status !== 'all') params.set('status', status)
      if (teacherId.trim()) params.set('teacher_id', teacherId.trim())
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/teacher/reimbursements?${params.toString()}`, {
        headers: { ...bearer },
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.error || 'Failed to load reimbursements')
      return { items: payload.items || [], total: payload.total || 0 }
    }
  }, [bearer, status, teacherId])

  const { items, page, pageCount, load, loading, error } = usePagedQuery<ReimbursementRequest>(fetcher, [fetcher], 20)

  const decide = async (request: ReimbursementRequest, nextStatus: string) => {
    if (!bearer) return
    const CORE_API_D = import.meta.env.VITE_CORE_API_URL || '';
    const response = await fetch(`${CORE_API_D}/api/v1/teacher/reimbursements/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({ request_id: request.id, status: nextStatus }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      alert(payload?.error || 'Unable to update request')
      return
    }
    show(`Marked ${nextStatus}`)
    load(page)
  }

  useEffect(() => {
    if (!selected) return
    const latest = items.find((request) => request.id === selected.id)
    if (latest && latest !== selected) setSelected(latest)
  }, [items, selected])

  return (
    <Section
      title="Reimbursements"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-cocoa/60">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setStatus(option)
                  load(1)
                }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  status === option ? 'bg-cocoa text-white' : 'bg-shell/60 text-cocoa/70 hover:bg-shell'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={teacherId}
            onChange={(event) => setTeacherId(event.target.value)}
            placeholder="Teacher ID"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
          >
            Apply
          </button>
        </div>
      }
    >
      <ToastHost />
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div>
          {loading ? (
            <p className="text-sm text-cocoa/70">Loading requests…</p>
          ) : error ? (
            <p className="text-sm text-brick">{error}</p>
          ) : items.length ? (
            <TableScroller>
              <table className="min-w-full divide-y divide-white/60 text-sm text-cocoa/80">
                <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
                  <tr>
                    <th className="px-3 py-2 text-left">Request</th>
                    <th className="px-3 py-2 text-left">Teacher</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-left">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/60">
                  {items.map((request) => (
                    <tr
                      key={request.id}
                      className={`cursor-pointer transition ${selected?.id === request.id ? 'bg-shell/60' : 'hover:bg-shell/40'}`}
                      onClick={() => setSelected(request)}
                    >
                      <td className="px-3 py-3 font-mono text-xs">{request.id}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">{request.teacher_id}</td>
                      <td className="px-3 py-3 capitalize">{request.status}</td>
                      <td className="px-3 py-3 text-right font-semibold text-cocoa">{formatCurrency(request.total_cents)}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">
                        {request.submitted_at ? new Date(request.submitted_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          ) : (
            <p className="text-sm text-cocoa/70">No reimbursement requests.</p>
          )}
          <div className="mt-4 flex justify-end gap-3 text-xs uppercase tracking-wide text-cocoa/60">
            <button
              type="button"
              onClick={() => load(Math.max(page - 1, 1))}
              disabled={page <= 1}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => load(Math.min(page + 1, pageCount))}
              disabled={page >= pageCount}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {selected ? (
            <div className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
              <header className="space-y-1">
                <h2 className="text-lg font-semibold text-cocoa">Request {selected.id}</h2>
                <p className="text-sm text-cocoa/60">Teacher {selected.teacher_id}</p>
                <p className="text-xs uppercase tracking-wide text-cocoa/50">Status: {selected.status}</p>
              </header>
              <div className="mt-4 space-y-3">
                {(selected.purchase_request_items || []).map((item) => (
                  <div key={item.id} className="rounded-xl border border-white/70 bg-shell/40 p-3 text-sm text-cocoa/80">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-cocoa">{item.description}</span>
                      <span className="font-semibold text-cocoa">{formatCurrency(item.amount_cents)}</span>
                    </div>
                    {item.receipt_url ? (
                      <a
                        href={item.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex text-xs font-semibold text-terracotta hover:underline"
                      >
                        View receipt
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => decide(selected, 'approved')}
                  className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => decide(selected, 'paid')}
                  className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  onClick={() => decide(selected, 'rejected')}
                  className="rounded-full bg-brick px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Reject
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/70 p-6 text-sm text-cocoa/60">
              Select a request to review details.
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}
